import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Users, 
  Plus, 
  Loader2,
  Trash2,
  Mail,
  UserCheck,
  UserX,
  Copy,
  Clock,
  RefreshCw,
  Pencil,
  Phone
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function UsersPage() {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', phone: '', role: '' });
  const [editSaving, setEditSaving] = useState(false);
  const { getAuthHeader, user: currentUser } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, invitesRes] = await Promise.all([
        axios.get(`${API}/users`, { headers: getAuthHeader() }),
        axios.get(`${API}/invites`, { headers: getAuthHeader() })
      ]);
      setUsers(usersRes.data);
      setInvites(invitesRes.data);
    } catch (err) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);

    try {
      const response = await axios.post(`${API}/invites`, {
        email: inviteEmail,
        role: inviteRole
      }, {
        headers: getAuthHeader()
      });
      
      toast.success('Convite enviado!');
      setDialogOpen(false);
      setInviteEmail('');
      setInviteRole('user');
      fetchData();

      // Copy invite link
      const inviteLink = `${window.location.origin}/invite/${response.data.invite_token}`;
      navigator.clipboard.writeText(inviteLink);
      toast.info('Link do convite copiado para a área de transferência');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar convite');
    } finally {
      setInviting(false);
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/invites/${inviteId}`, {
        headers: getAuthHeader()
      });
      toast.success('Convite cancelado');
      fetchData();
    } catch (err) {
      toast.error('Erro ao cancelar convite');
    }
  };

  const handleToggleUser = async (userId, isActive) => {
    try {
      await axios.put(`${API}/users/${userId}`, {
        is_active: !isActive
      }, {
        headers: getAuthHeader()
      });
      toast.success(isActive ? 'Usuário desativado' : 'Usuário ativado');
      fetchData();
    } catch (err) {
      toast.error('Erro ao atualizar usuário');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Tem certeza que deseja remover este usuário?')) return;

    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: getAuthHeader()
      });
      toast.success('Usuário removido');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao remover usuário');
    }
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copiado!');
  };

  const handleResendInvite = async (inviteId) => {
    try {
      const res = await axios.post(`${API}/invites/${inviteId}/resend`, {}, {
        headers: getAuthHeader()
      });
      const newToken = res.data.invite_token;
      const link = `${window.location.origin}/invite/${newToken}`;
      navigator.clipboard.writeText(link);
      toast.success('Convite renovado e link copiado!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao reenviar convite');
    }
  };

  const openEditUser = (u) => {
    setEditUser(u);
    setEditForm({ full_name: u.full_name || '', phone: u.phone || '', role: u.role || 'user' });
  };

  const handleSaveEditUser = async () => {
    if (!editUser) return;
    setEditSaving(true);
    try {
      await axios.put(`${API}/users/${editUser.id}`, editForm, { headers: getAuthHeader() });
      toast.success('Usuário atualizado');
      setEditUser(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Gestão de Usuários</h2>
          <p className="text-sm text-muted-foreground">
            Gerencie usuários e convites do seu tenant
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-btn">
              <Plus className="mr-2 h-4 w-4" />
              Convidar Usuário
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleInvite}>
              <DialogHeader>
                <DialogTitle>Convidar Usuário</DialogTitle>
                <DialogDescription>
                  Envie um convite para adicionar um novo membro à equipe
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@empresa.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    data-testid="invite-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Função</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Usuário</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Administradores podem gerenciar configurações e usuários
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={inviting} data-testid="send-invite-btn">
                  {inviting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Enviar Convite
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            Usuários ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Função</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                  <TableCell className="font-medium">{user.full_name}</TableCell>
                  <TableCell className="text-muted-foreground">{user.email}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{user.phone || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                      {user.role === 'admin' ? 'Admin' : 'Usuário'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.is_active ? (
                      <Badge variant="default" className="bg-green-500">
                        <UserCheck className="mr-1 h-3 w-3" />
                        Ativo
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <UserX className="mr-1 h-3 w-3" />
                        Inativo
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {user.id !== currentUser?.id ? (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditUser(user)} data-testid={`edit-user-${user.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Switch
                          checked={user.is_active}
                          onCheckedChange={() => handleToggleUser(user.id, user.is_active)}
                          data-testid={`toggle-user-${user.id}`}
                        />
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(user.id)} data-testid={`delete-user-${user.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Você</span>
                    )}
                    {user.id === currentUser?.id && (
                      <span className="text-xs text-muted-foreground">Você</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pending Invites */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Convites Pendentes ({invites.filter(i => !i.used).length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {invites.filter(i => !i.used).length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Função</TableHead>
                  <TableHead>Enviado em</TableHead>
                  <TableHead>Expira em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.filter(i => !i.used).map((invite) => (
                  <TableRow key={invite.id} data-testid={`invite-row-${invite.id}`}>
                    <TableCell>{invite.email}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {invite.role === 'admin' ? 'Admin' : 'Usuário'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(invite.created_at)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(invite.expires_at)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleResendInvite(invite.id)}
                          title="Reenviar convite"
                          data-testid={`resend-invite-${invite.id}`}
                        >
                          <RefreshCw className="h-4 w-4 text-blue-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => copyInviteLink(invite.token)}
                          title="Copiar link"
                          data-testid={`copy-invite-${invite.id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteInvite(invite.id)}
                          title="Cancelar convite"
                          data-testid={`delete-invite-${invite.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Nenhum convite pendente</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={(v) => { if (!v) setEditUser(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Usuário</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome Completo</Label>
              <Input value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} data-testid="edit-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={editUser?.email || ''} disabled className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />Telefone / Celular</Label>
              <Input placeholder="+55 (11) 99999-9999" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-user-phone" />
            </div>
            <div className="space-y-2">
              <Label>Função</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">Usuário</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Cancelar</Button>
            <Button onClick={handleSaveEditUser} disabled={editSaving} data-testid="save-edit-user-btn">
              {editSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
